import axios from 'axios';

import { FileDto } from '@tallo/file-manager';

import {
  AttachUnitDto,
  BuildingDto,
  BuildingSection,
  BuildingStatisticsDto,
  CreateBuildingDto,
  UnitDefaultSection,
  UpdateBuildingDto,
} from '../dtos/building.dto.interface';

class BuildingService {
  /** Every building of the company, oldest first */
  async list(): Promise<BuildingDto[]> {
    return (await axios.get<BuildingDto[]>('building')).data;
  }

  // The id comes from the address bar, so it is kept to one path segment
  async getById(buildingId: string): Promise<BuildingDto> {
    return (await axios.get<BuildingDto>(`building/${encodeURIComponent(buildingId)}`)).data;
  }

  async create(payload: CreateBuildingDto): Promise<BuildingDto> {
    return (await axios.post<BuildingDto>('building', payload)).data;
  }

  async update(buildingId: string, payload: UpdateBuildingDto): Promise<BuildingDto> {
    return (await axios.patch<BuildingDto>(`building/${encodeURIComponent(buildingId)}`, payload)).data;
  }

  /** Saves one shared section on the building; every unit follows in the same call */
  async saveSection(buildingId: string, section: BuildingSection, payload: object): Promise<void> {
    const path = section === 'location' ? 'property-location' : section;

    await axios.put(`building/${encodeURIComponent(buildingId)}/${path}`, payload);
  }

  /** Only a building with no units left goes; the server refuses the rest with BUILDING_HAS_UNITS */
  async remove(buildingId: string): Promise<void> {
    await axios.delete(`building/${encodeURIComponent(buildingId)}`);
  }

  /** Adds one photo after the last; the first photo is the cover. Answers the whole list in order */
  async uploadImage(buildingId: string, image: File): Promise<FileDto[]> {
    const formData = new FormData();
    formData.append('image', image);

    return (await axios.post<FileDto[]>(`building/${encodeURIComponent(buildingId)}/images`, formData)).data;
  }

  async deleteImage(buildingId: string, imageId: string): Promise<FileDto[]> {
    return (
      await axios.delete<FileDto[]>(`building/${encodeURIComponent(buildingId)}/images/${encodeURIComponent(imageId)}`)
    ).data;
  }

  /** Every photo's id, in the order wanted; the first becomes the cover */
  async updateImagesOrder(buildingId: string, imageIds: string[]): Promise<FileDto[]> {
    return (await axios.patch<FileDto[]>(`building/${encodeURIComponent(buildingId)}/images/order`, { imageIds })).data;
  }

  /** Sets what a new unit starts with; units already added keep theirs */
  async saveUnitDefault(buildingId: string, section: UnitDefaultSection, payload: object): Promise<void> {
    const path = {
      renterRequirements: 'renter-requirements',
      petPolicy: 'pet-policy',
      leaseConditions: 'lease-conditions',
      tourTypes: 'tour-types',
    }[section];

    await axios.put(`building/${encodeURIComponent(buildingId)}/${path}`, payload);
  }

  /** Puts a photo on the building in place of its cover */
  async uploadCover(buildingId: string, image: File): Promise<BuildingDto> {
    const formData = new FormData();
    formData.append('image', image);

    return (await axios.post<BuildingDto>(`building/${encodeURIComponent(buildingId)}/cover`, formData)).data;
  }

  async getStatistics(buildingId: string): Promise<BuildingStatisticsDto> {
    return (await axios.get<BuildingStatisticsDto>(`building/${encodeURIComponent(buildingId)}/statistics`)).data;
  }

  /** Files standing apartments into the building as its units, all or none; a taken number answers UNIT_NUMBER_TAKEN */
  async attachUnits(buildingId: string, units: AttachUnitDto[]): Promise<void> {
    await axios.post(`building/${encodeURIComponent(buildingId)}/units/attach`, { units });
  }

  /** Takes a unit out of its building; it stands on its own again with everything it holds */
  async detachUnit(buildingId: string, propertyId: string): Promise<void> {
    await axios.delete(`building/${encodeURIComponent(buildingId)}/units/${encodeURIComponent(propertyId)}`);
  }

  /** Starts a draft unit on the building's address with its number; the property wizard does the rest */
  async createUnit(buildingId: string, unitNumber: string): Promise<{ id: string }> {
    return (await axios.post<{ id: string }>(`building/${encodeURIComponent(buildingId)}/units`, { unitNumber })).data;
  }
}

export default new BuildingService();
